/**
 * One gate for every desktop notification about a conversation, so the
 * three sources (the live session stream, background asks, the org event
 * stream / queue poll) never double up and never interrupt the creator about
 * the conversation they are looking at.
 */
import type { AgentEventEnvelope, AnyAgentEvent } from '@/agent/protocol/types'
import { desktopNotify, desktopRequestAttention, isDesktopApp } from './bridge'
import { desktopClientId } from './clientTools'
import { clientCallNotification, notificationForSessionEvent } from './notificationRules'
import type { DesktopNotification } from './types'

/** One notification per conversation and kind: repeats inside this window are dropped. */
const DEDUPE_WINDOW_MS = 2 * 60_000
const recent = new Map<string, number>()

/** True while the creator is looking at the app (window focused and visible). */
export function appIsInFront(): boolean {
    if (typeof document === 'undefined') return false
    return !document.hidden && document.hasFocus()
}

/** The conversation open on screen, from the URL (null elsewhere). */
export function openConversationId(): string | null {
    if (typeof window === 'undefined') return null
    const m = window.location.pathname.match(/^\/dashboard\/conversations\/([^/?#]+)/)
    return m ? decodeURIComponent(m[1]) : null
}

/** Conversations a mounted conversation surface shows (the assistant panel and Home have no URL for them). */
const onScreen = new Map<string, number>()

/** A conversation surface is showing this session; call the returned function when it stops. */
export function markConversationOnScreen(sessionId: string): () => void {
    onScreen.set(sessionId, (onScreen.get(sessionId) ?? 0) + 1)
    let released = false
    return () => {
        if (released) return
        released = true
        const left = (onScreen.get(sessionId) ?? 1) - 1
        if (left > 0) onScreen.set(sessionId, left)
        else onScreen.delete(sessionId)
    }
}

/** True when the conversation is on screen: its page, or a panel / surface showing it. */
export function isConversationOnScreen(sessionId: string): boolean {
    return openConversationId() === sessionId || onScreen.has(sessionId)
}

/**
 * Notify about a conversation unless it was just notified, or the creator is
 * already looking at it. Returns whether a notification was sent.
 */
export function notifyAboutSession(sessionId: string, notification: DesktopNotification, now = Date.now()): boolean {
    if (!isDesktopApp()) return false
    if (appIsInFront() && isConversationOnScreen(sessionId)) return false
    if (!claim(sessionId, notification, now)) return false
    // Checked above that it isn't on screen, so the shell may show it while Lighthouse is in front.
    desktopNotify({ ...notification, whileFocused: true })
    return true
}

function claim(sessionId: string, notification: DesktopNotification, now: number): boolean {
    const key = `${sessionId}:${notification.kind ?? 'agent'}`
    const last = recent.get(key)
    if (last && now - last < DEDUPE_WINDOW_MS) return false
    recent.set(key, now)
    for (const [k, at] of recent) if (now - at > DEDUPE_WINDOW_MS) recent.delete(k)
    return true
}

/** Desktop asks already announced (the live session, a background follow and the org stream may all see one). */
const announcedCalls = new Set<string>()

/**
 * A `client.call` for this window (its `client_id`): the agent is waiting on the
 * creator's OK. Unless they are looking at the conversation, notify (an
 * approval: the card has the buttons) and, with the window in the background,
 * bounce the dock / flash the taskbar. Each ask is announced once.
 */
export function notifyClientCall(ev: AgentEventEnvelope<'client.call'>, sessionId: string | null): boolean {
    if (!sessionId || !isDesktopApp()) return false
    const p = ev.payload
    if (!p?.call_id || p.client_id !== desktopClientId() || announcedCalls.has(p.call_id)) return false
    const inFront = appIsInFront()
    if (inFront && isConversationOnScreen(sessionId)) return false
    const expires = Date.parse(p.expires_at)
    if (Number.isFinite(expires) && expires <= Date.now()) return false
    announcedCalls.add(p.call_id)
    desktopNotify({ ...clientCallNotification(p, sessionId), whileFocused: true })
    if (!inFront) desktopRequestAttention()
    return true
}

/** Forget dedupe state (sign-out, tests). */
export function resetNotificationDedupe(): void {
    recent.clear()
    announcedCalls.clear()
}

/**
 * useAgentSession: a live `run.finished` in a conversation that is mounted
 * on screen. Only worth a notification when the creator has switched away
 * (window hidden or not focused) while the agent worked.
 */
export function notifyLiveRunFinished(
    ev: AnyAgentEvent,
    ctx: { sessionId: string | null; title?: string | null; lastAssistantText?: string | null },
): void {
    if (!ctx.sessionId || ev.type !== 'run.finished' || !isDesktopApp()) return
    if (typeof document !== 'undefined' && !document.hidden && document.hasFocus()) return
    const n = notificationForSessionEvent(ev, { sessionId: ctx.sessionId, title: ctx.title, lastAssistantText: ctx.lastAssistantText })
    if (!n) return
    // The window is in the background, so "looking at it" does not apply; only dedupe.
    if (!claim(ctx.sessionId, n, Date.now())) return
    desktopNotify({ ...n, tag: `session-${ctx.sessionId}` })
}
